import { faCircleInfo, faCirclePlus, faPenToSquare, faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faAngleUp } from "@fortawesome/sharp-thin-svg-icons"
import Box from "@mui/material/Box"
import Collapse from "@mui/material/Collapse"
import Typography from "@mui/material/Typography"
import React from "react"

import Button from "@mui/material/Button"
import Checkbox from "components/Checkbox/Checkbox"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiAsyncAutoComplete from "components/MuiAsyncAutocomplete"
import CountrySelectField from "components/MuiCountrySelect"
import MuiModal from "components/MuiModal"
import MuiTextField from "components/MuiTextField"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { ProjectFormPageProps } from "components/ProjectForm"
import ProjectFormFooter from "components/ProjectForm/components/ProjectFormFooter"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import { EAccountRole } from "enums/commonEnums"
import { ESubModuleIds } from "enums/ModuleEnum"
import AddAccounts from "pages/Accounts/features/AddAccounts"
import Sites from "./Sites"
import useAccountNSites, { EAccountFormFields, EAccountRoleFields } from "./useAccountNSites"

type Option = {
    id: string | number
    title: string
    disabled?: boolean
}

const WAREHOUSE_OPTIONS = [
    { id: 0, title: "No" },
    { id: 1, title: "Yes" },
]

type AccountSectionWrapperProps = {
    children: React.ReactNode
    title: EAccountRole
    isDeletable?: boolean
    onDelete?: () => void
}

const AccountSectionWrapper = ({ children, title, isDeletable = false, onDelete }: AccountSectionWrapperProps) => {
    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            bgcolor={"#FAFAFA"}
            borderRadius={"8px"}
            padding={"12px"}>
            <Box display={"flex"} alignItems={"center"} gap={2}>
                <Typography variant="label" fontSize={"14px"} fontWeight={500} textTransform={"uppercase"}>
                    {title} Account
                </Typography>
                {isDeletable && (
                    <Box
                        width={"20px"}
                        height={"20px"}
                        display={"flex"}
                        justifyContent={"center"}
                        borderRadius={"50%"}
                        bgcolor={"#F940541A"}
                        alignItems={"center"}
                        sx={{
                            cursor: "pointer",
                        }}
                        onClick={onDelete}>
                        <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="xs" />
                    </Box>
                )}
            </Box>
            {children}
        </Box>
    )
}

type AccountRoleFieldsProps = {
    role: EAccountRole
    accountsTrigger: (arg: any) => { unwrap: () => Promise<any> }
    billingAddressOptions: Option[]
    contactOptions: Option[]
    accountValue: Option | null
    billingAddressValue: Option | null
    contactValue: Option | null
    onAccountChange: (role: EAccountRole, value: Option | null) => void
    onBillingAddressChange: (role: EAccountRole, value: Option | null) => void
    onContactChange: (role: EAccountRole, value: Option | null) => void
    onDelete?: () => void
    isDeletable?: boolean
    accountError?: boolean
    billingAddressError?: boolean
    contactError?: boolean
    isLoading?: boolean
    onAddAccount?: (role: EAccountRole) => void
    onEditAccount?: (role: EAccountRole) => void
    disableAccount?: boolean
}

const AccountRoleFields = ({
    role,
    accountsTrigger,
    billingAddressOptions,
    contactOptions,
    accountValue,
    billingAddressValue,
    contactValue,
    onAccountChange,
    onBillingAddressChange,
    onContactChange,
    onDelete,
    isDeletable = false,
    accountError = false,
    billingAddressError = false,
    contactError = false,
    isLoading = false,
    onAddAccount,
    onEditAccount,
    disableAccount = false,
}: AccountRoleFieldsProps) => {
    return (
        <AccountSectionWrapper title={role} isDeletable={isDeletable} onDelete={onDelete}>
            <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2} width={"100%"}>
                <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={0.5}>
                        <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                            Account Name*
                        </Typography>
                        {onAddAccount && (
                            <FontAwesomeIcon
                                color={"#404767"}
                                className="cursor-pointer text-sm"
                                icon={faCirclePlus}
                                onClick={() => onAddAccount(role)}
                            />
                        )}
                    </Box>
                    <MuiAsyncAutoComplete
                        disablePortal={false}
                        trigger={accountsTrigger}
                        buildQueryArg={({ page, page_size, search }) => ({ page, page_size, search_key: search })}
                        selectData={(res: any) => ({
                            options: res.data.map((a: any) => ({ id: a.id, title: a.account_name })),
                            hasMore: res.have_more,
                        })}
                        value={accountValue}
                        placeholder="Select Account Name"
                        size="large"
                        disableClearable
                        disabled={disableAccount}
                        onChange={value => onAccountChange(role, value as Option | null)}
                        error={accountError}
                    />
                </Box>
                <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={0.5}>
                        <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                            Billing Address*
                        </Typography>
                        {accountValue && onEditAccount && (
                            <FontAwesomeIcon
                                icon={faPenToSquare}
                                color={"#404767"}
                                className="cursor-pointer text-sm"
                                onClick={() => onEditAccount(role)}
                            />
                        )}
                    </Box>
                    <MuiAutoComplete
                        disablePortal={false}
                        options={billingAddressOptions}
                        value={billingAddressValue}
                        placeholder="Select Billing Address"
                        size="large"
                        disableClearable
                        loading={isLoading}
                        disabled={!accountValue || isLoading}
                        onChange={value => onBillingAddressChange(role, value as Option | null)}
                        error={billingAddressError}
                    />
                </Box>
                <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={0.5}>
                        <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                            Contact*
                        </Typography>
                        {accountValue && onEditAccount && (
                            <FontAwesomeIcon
                                icon={faPenToSquare}
                                color={"#404767"}
                                className="cursor-pointer text-sm"
                                onClick={() => onEditAccount(role)}
                            />
                        )}
                    </Box>
                    <MuiAutoComplete
                        disablePortal={false}
                        options={contactOptions}
                        value={contactValue}
                        placeholder="Select Contact"
                        size="large"
                        loading={isLoading}
                        disableClearable
                        disabled={!accountValue || isLoading}
                        onChange={value => onContactChange(role, value as Option | null)}
                        error={contactError}
                    />
                </Box>
            </Box>
        </AccountSectionWrapper>
    )
}

type AccountNSitesProps = ProjectFormPageProps & {
    hasThreeDefaultRoles?: boolean
}

const AccountNSites = (props: AccountNSitesProps) => {
    const { hasThreeDefaultRoles = false } = props
    const { accountSitesRules, isFetching: isUIRulesLoading } = useTemplateUIRules()

    const showSourceSite = props.subModuleId === ESubModuleIds.SURVEY ? false : accountSitesRules?.show_source_site
    const { states, functions } = useAccountNSites(hasThreeDefaultRoles, showSourceSite, props.subModuleId)

    const handleSubmit = () => {
        states.formik.handleSubmit()

        if (states.isFormValid) {
            functions.submitAccountNSitesForm()
            props.handleNextStep()
        }
    }

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={2}
                className="custom-scrollbar"
                maxHeight={"62vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                {/* SITE DETAILS */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box display={"flex"} gap={"16px"} alignItems={"center"}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            SITE DETAILS
                        </Typography>
                        {!states.showNewSiteForm && states.addSitesPermission && (
                            <Button
                                variant="text"
                                onClick={functions.toggleNewSiteForm}
                                sx={{
                                    color: "#404767",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    display: "flex",
                                    gap: "4px",
                                    alignItems: "center",
                                }}>
                                <FontAwesomeIcon icon={faCirclePlus} />
                                Add New Site
                            </Button>
                        )}
                    </Box>

                    {/* New Site Form */}
                    {states.showNewSiteForm ? (
                        <Box
                            bgcolor={"#FAFAFA"}
                            borderRadius={"8px"}
                            padding={"16px"}
                            display={"flex"}
                            flexDirection={"column"}
                            gap={2}>
                            <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                                NEW SITE
                            </Typography>
                            <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Country*
                                    </Typography>
                                    <Box
                                        sx={{
                                            "& .MuiInputBase-root": { height: "44px" },
                                            ".MuiAutocomplete": { height: "44px" },
                                        }}>
                                        <CountrySelectField
                                            value={states.newSiteDetails.country}
                                            onChange={(val: any) => functions.handleNewSiteFieldChange("country", val)}
                                            required={true}
                                            showError={states.showNewSiteErrors}
                                        />
                                    </Box>
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Site Name*
                                    </Typography>
                                    <MuiTextField
                                        placeholder="Enter Name"
                                        fullWidth
                                        size="large"
                                        value={states.newSiteDetails.siteName}
                                        onChange={e => functions.handleNewSiteFieldChange("siteName", e.target.value)}
                                        error={states.showNewSiteErrors && !states.newSiteDetails.siteName}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Site Address*
                                    </Typography>
                                    <MuiTextField
                                        fullWidth
                                        placeholder="Enter Address"
                                        size="large"
                                        value={states.newSiteDetails.siteAddress}
                                        onChange={e =>
                                            functions.handleNewSiteFieldChange("siteAddress", e.target.value)
                                        }
                                        error={states.showNewSiteErrors && !states.newSiteDetails.siteAddress}
                                    />
                                </Box>
                            </Box>
                            <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Pincode*
                                    </Typography>
                                    <MuiTextField
                                        fullWidth
                                        placeholder="Enter Pincode"
                                        size="large"
                                        value={states.newSiteDetails.pincode}
                                        onChange={e => functions.handleNewSiteFieldChange("pincode", e.target.value)}
                                        error={states.showNewSiteErrors && !states.newSiteDetails.pincode}
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: states.newSiteDetails.country?.id === 101 ? 6 : 20,
                                            },
                                        }}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        State*
                                    </Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        placeholder="Select State"
                                        disableClearable
                                        options={states.statesOptions}
                                        value={states.newSiteDetails.state}
                                        onChange={(value: any) => functions.handleNewSiteFieldChange("state", value)}
                                        size="large"
                                        error={states.showNewSiteErrors && !states.newSiteDetails.state}
                                        disabled={!states.newSiteDetails.country?.id}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        City*
                                    </Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        placeholder="Select City"
                                        disableClearable
                                        options={states.citiesOptions}
                                        value={states.newSiteDetails.city}
                                        onChange={(value: any) => functions.handleNewSiteFieldChange("city", value)}
                                        size="large"
                                        error={states.showNewSiteErrors && !states.newSiteDetails.city}
                                        disabled={!states.newSiteDetails.state?.id}
                                    />
                                </Box>
                            </Box>
                            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} alignItems="center">
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Coordinates*
                                    </Typography>
                                    <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                        <MuiTextField
                                            placeholder="Latitude (-90 to 90)"
                                            size="large"
                                            type="number"
                                            value={states.newSiteDetails.latitude}
                                            onChange={e =>
                                                functions.handleNewSiteFieldChange("latitude", e.target.value)
                                            }
                                            onPaste={e => functions.handleCoordinatePaste(e, "latitude")}
                                            error={
                                                states.showNewSiteErrors &&
                                                (!states.newSiteDetails.latitude ||
                                                    !functions.isValidLatitude(states.newSiteDetails.latitude))
                                            }
                                        />
                                        <MuiTextField
                                            placeholder="Longitude (-180 to 180)"
                                            size="large"
                                            type="number"
                                            value={states.newSiteDetails.longitude}
                                            onChange={e =>
                                                functions.handleNewSiteFieldChange("longitude", e.target.value)
                                            }
                                            onPaste={e => functions.handleCoordinatePaste(e, "longitude")}
                                            error={
                                                states.showNewSiteErrors &&
                                                (!states.newSiteDetails.longitude ||
                                                    !functions.isValidLongitude(states.newSiteDetails.longitude))
                                            }
                                        />
                                    </Box>
                                </Box>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography variant="label" fontSize={"12px"}>
                                        Is Warehouse?
                                    </Typography>
                                    <RadioGroup
                                        name="is_warehouse"
                                        options={WAREHOUSE_OPTIONS}
                                        value={
                                            WAREHOUSE_OPTIONS.find(
                                                option => option.id === states.newSiteDetails.isWarehouse
                                            ) || WAREHOUSE_OPTIONS[0]
                                        }
                                        onSelect={(value: Option) =>
                                            functions.handleNewSiteFieldChange("isWarehouse", value.id)
                                        }
                                    />
                                </Box>
                            </Box>
                            <Box display={"flex"} justifyContent={"flex-end"}>
                                <Button
                                    variant="text"
                                    onClick={functions.toggleNewSiteForm}
                                    sx={{
                                        fontSize: "12px",
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={functions.handleAddNewSite}
                                    disabled={states.isAddingSite}
                                    sx={{
                                        bgcolor: "#FC441E",
                                        "&:hover": { bgcolor: "#e03d1a" },
                                        textTransform: "none",
                                    }}>
                                    {states.isAddingSite ? "Adding..." : "Add Site"}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {/* Existing Site Selection */}
                            <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2} width={"100%"}>
                                <Box
                                    display={"flex"}
                                    flexDirection={"column"}
                                    gap={1}
                                    sx={{ order: props.subModuleId === ESubModuleIds.DEMOLITION ? 2 : 1 }}>
                                    <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                                        {props.subModuleId === ESubModuleIds.SURVEY
                                            ? "Survey Site*"
                                            : "Destination Site*"}
                                    </Typography>
                                    <Box
                                        borderBottom={"0.5px solid #E5E7EB"}
                                        minWidth={"250px"}
                                        height={"44px"}
                                        display={"flex"}
                                        alignItems={"center"}
                                        justifyContent={"space-between"}
                                        gap={2}
                                        padding={"0 8px"}
                                        bgcolor={"#E8E8EC33"}
                                        onClick={functions.toggleDestinationSites}
                                        sx={{
                                            cursor: "pointer",
                                            borderColor: states.destinationSiteError
                                                ? "#F94054"
                                                : states.showDestinationSites
                                                  ? "#101941"
                                                  : "#E5E7EB",
                                            "&:hover": {
                                                borderColor: states.destinationSiteError ? "#F94054" : "#101941",
                                            },
                                        }}>
                                        {states.destinationSite ? (
                                            <Box display={"flex"} alignItems={"center"} gap={1} flex={1}>
                                                <Typography
                                                    fontSize={"12px"}
                                                    variant="label"
                                                    color={"#70758D"}
                                                    fontWeight={400}>
                                                    {states.destinationSite.name}
                                                </Typography>
                                                <Box
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        states.formik.setFieldValue(
                                                            EAccountFormFields.DESTINATION_SITE,
                                                            null
                                                        )
                                                    }}
                                                    sx={{
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        marginLeft: "auto",
                                                    }}></Box>
                                            </Box>
                                        ) : (
                                            <Typography
                                                fontSize={"12px"}
                                                variant="label"
                                                color={"#B7BAC6"}
                                                fontWeight={400}>
                                                {props.subModuleId === ESubModuleIds.SURVEY
                                                    ? "Select Survey Site"
                                                    : "Select Destination Site"}
                                            </Typography>
                                        )}
                                        <FontAwesomeIcon
                                            icon={states.showDestinationSites ? faAngleUp : faAngleDown}
                                            color={states.showDestinationSites ? "#101941" : "#B7BAC6"}
                                        />
                                    </Box>
                                </Box>
                                {showSourceSite && (
                                    <Box
                                        display={"flex"}
                                        flexDirection={"column"}
                                        gap={1}
                                        sx={{ order: props.subModuleId === ESubModuleIds.DEMOLITION ? 1 : 2 }}>
                                        <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                                            Source Site*
                                        </Typography>
                                        <Box
                                            borderBottom={"0.5px solid #E5E7EB"}
                                            minWidth={"250px"}
                                            height={"44px"}
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"space-between"}
                                            gap={2}
                                            padding={"0 8px"}
                                            bgcolor={"#E8E8EC33"}
                                            onClick={functions.toggleSourceSites}
                                            sx={{
                                                cursor: "pointer",
                                                borderColor: states.sourceSiteError
                                                    ? "#F94054"
                                                    : states.showSourceSites
                                                      ? "#101941"
                                                      : "#E5E7EB",
                                                "&:hover": {
                                                    borderColor: states.sourceSiteError ? "#F94054" : "#101941",
                                                },
                                            }}>
                                            {states.sourceSite ? (
                                                <Box display={"flex"} alignItems={"center"} gap={1} flex={1}>
                                                    <Typography
                                                        fontSize={"12px"}
                                                        variant="label"
                                                        color={"#70758D"}
                                                        fontWeight={400}>
                                                        {states.sourceSite.name}
                                                    </Typography>
                                                    <Box
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            states.formik.setFieldValue(
                                                                EAccountFormFields.SOURCE_SITE,
                                                                null
                                                            )
                                                        }}
                                                        sx={{
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            marginLeft: "auto",
                                                        }}></Box>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    fontSize={"12px"}
                                                    variant="label"
                                                    color={"#B7BAC6"}
                                                    fontWeight={400}>
                                                    Select Source Site
                                                </Typography>
                                            )}
                                            <FontAwesomeIcon
                                                icon={states.showSourceSites ? faAngleUp : faAngleDown}
                                                color={states.showSourceSites ? "#101941" : "#B7BAC6"}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Destination Sites Selection */}
                            <Collapse in={states.showDestinationSites}>
                                <Sites
                                    type="destination"
                                    selectedSiteId={states.destinationSite?.id || null}
                                    onSiteSelect={functions.handleDestinationSiteSelect}
                                />
                            </Collapse>

                            {/* Source Sites Selection */}
                            {showSourceSite && (
                                <Collapse in={states.showSourceSites}>
                                    <Sites
                                        type="source"
                                        selectedSiteId={states.sourceSite?.id || null}
                                        onSiteSelect={functions.handleSourceSiteSelect}
                                        pinnedSiteIds={accountSitesRules.source_site_pin}
                                        defaultSiteId={accountSitesRules.source_site_default}
                                    />
                                </Collapse>
                            )}
                        </>
                    )}
                </Box>

                {/* ACCOUNT DETAILS */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                        ACCOUNT DETAILS
                    </Typography>

                    {props.isCPODeal && (
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                            gap={"11px"}
                            bgcolor={"#FAFAFA"}
                            borderRadius={"8px"}
                            padding={"10px 12px"}>
                            <Checkbox
                                id="franchisePartnerCheckbox"
                                checked={states.isFranchisePartner}
                                disabled={states.isFranchisePartnerLocked}
                                onChange={functions.handleFranchisePartnerToggle}
                            />
                            <Typography
                                component="label"
                                htmlFor="franchisePartnerCheckbox"
                                fontSize={"14px"}
                                fontWeight={600}
                                sx={{ cursor: states.isFranchisePartnerLocked ? "default" : "pointer" }}>
                                Is this Account a Franchise Partner?
                            </Typography>
                            {states.isFranchisePartnerLocked && (
                                <Typography color="#6B7688" fontSize={"12px"}>
                                    Already mapped to a Franchise Partner — Buyer is prefilled and locked.
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        gap={1}
                        sx={{ width: { xs: "100%", sm: "50%", md: "25%" } }}>
                        <Typography variant="label" fontSize={"14px"} fontWeight={500} color={"#0430C0"}>
                            Select New Account Role
                            <FontAwesomeIcon icon={faCircleInfo} color="#A8A8A8" className="ml-2" />
                        </Typography>
                        <MuiAutoComplete
                            disablePortal={false}
                            options={states.accountRolesOptions}
                            value={states.selectedRoles}
                            placeholder="Select"
                            multiple={true}
                            size="large"
                            disableClearable
                            onChange={value => functions.handleRoleChange(value as Option[] | null)}
                        />
                    </Box>

                    {/* Dynamic Account Role Sections */}
                    {states.selectedRoles.map(role => {
                        const roleKey = role.title as EAccountRole
                        const roleData = states.formik.values[EAccountFormFields.ACCOUNT_ROLES][roleKey]
                        const roleOptions = states.roleOptionsMap[roleKey]
                        const isLoading = states.loadingMap[roleKey]
                        const isDeletable = !functions.isDefaultRole(roleKey)

                        return (
                            <AccountRoleFields
                                key={roleKey}
                                role={roleKey}
                                accountsTrigger={states.accountsTrigger}
                                billingAddressOptions={roleOptions?.billingAddresses || []}
                                contactOptions={roleOptions?.contacts || []}
                                accountValue={roleData?.[EAccountRoleFields.ACCOUNT] || null}
                                billingAddressValue={roleData?.[EAccountRoleFields.BILLING_ADDRESS] || null}
                                contactValue={roleData?.[EAccountRoleFields.CONTACT] || null}
                                onAccountChange={functions.handleAccountChange}
                                onBillingAddressChange={functions.handleBillingAddressChange}
                                onContactChange={functions.handleContactChange}
                                onDelete={() => functions.handleDeleteRole(roleKey)}
                                isDeletable={isDeletable}
                                accountError={!!functions.getFieldError(roleKey, EAccountRoleFields.ACCOUNT)}
                                billingAddressError={
                                    !!functions.getFieldError(roleKey, EAccountRoleFields.BILLING_ADDRESS)
                                }
                                contactError={!!functions.getFieldError(roleKey, EAccountRoleFields.CONTACT)}
                                isLoading={isLoading}
                                onAddAccount={
                                    states.hasAddAccountPermission ? functions.openAddAccountModal : undefined
                                }
                                onEditAccount={
                                    states.hasEditAccountPermission ? functions.openEditAccountModal : undefined
                                }
                                disableAccount={states.prefilledRoles.has(roleKey)}
                            />
                        )
                    })}
                </Box>
            </Box>
            <ProjectFormFooter handleCloseForm={props.handleCloseForm} handleSubmit={handleSubmit} />
            {states.showAccountFormModal && (
                <MuiModal
                    open={states.showAccountFormModal}
                    header="Please enter the details"
                    onClose={functions.closeAccountFormModal}
                    width={"671px"}
                    maxHeight={"80vh"}>
                    <AddAccounts
                        toggle={functions.closeAccountFormModal}
                        isShown={states.showAccountFormModal}
                        ticketData={states.editAccountData}
                        editFlag={states.isEditMode}
                        onSuccess={functions.handleAccountFormSuccess}
                        defaultAccountType={{ id: "Client", title: "Client" }}
                        disableAccountType
                    />
                </MuiModal>
            )}
        </>
    )
}

export default AccountNSites
